import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { TabsNodeView } from './tabs-node-view'
import { nanoid } from 'nanoid'

export interface TabsAttributes {
  id?: string
}


export const TabsSpec = {
  group: 'block',
  content: 'Tab+',
  attrs: { id: { default: nanoid(10) } },
  toDOM() { return ['div', { 'data-tabs': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-tabs]' }],
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tabs: {
      /**
       * Insert a tabs container
       */
      insertTabs: (attributes?: TabsAttributes) => ReturnType
      /**
       * Update tabs attributes
       */
      updateTabs: (attributes: Partial<TabsAttributes>) => ReturnType
    }
  }
}

export const TabsExtension = Node.create({
  name: 'Tabs',

  group: 'block',

  content: 'Tab+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-dg-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="Tabs"]',
      },
      {
        tag: 'div.tabs-container',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'Tabs',
        class: 'group/tabs relative my-5 p-0.5',
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(TabsNodeView)
  },

  addCommands() {
    return {
      insertTabs:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
            content: [
              {
                type: 'Tab',
                attrs: {
                  title: 'Tab 1',
                  icon: 'sparkles',
                  iconType: 'REGULAR',
                  iconColor: '#3b82f6',
                  showIcon: false,
                },
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      
                    ],
                  },
                ],
              },
              {
                type: 'Tab',
                attrs: {
                  title: 'Tab 2',
                  icon: 'sparkles',
                  iconType: 'REGULAR',
                  iconColor: '#3b82f6',
                  showIcon: false,
                },
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      
                    ],
                  },
                ],
              },
            ],
          })
        },
      updateTabs:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the tabs container with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of a tabs container
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
})
